
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Plus } from "lucide-react";
import { SelectOption } from "@/models";

interface SelectOptionsEditorProps {
  options: SelectOption[];
  onChange: (options: SelectOption[]) => void;
  disabled?: boolean;
}

const SelectOptionsEditor = ({
  options,
  onChange,
  disabled = false
}: SelectOptionsEditorProps) => {
  // Convert label to snake_case for value
  const toSnakeCase = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '') || 'option';
  };

  // Add a new empty option
  const handleAddOption = () => {
    const newOptionNumber = options.length + 1;
    onChange([...options, { label: `Option ${newOptionNumber}`, value: `option_${newOptionNumber}` }]);
  };

  // Remove an option at the specified index
  const handleRemoveOption = (index: number) => {
    const newOptions = [...options];
    newOptions.splice(index, 1);
    onChange(newOptions);
  };

  // Update an option at the specified index
  const handleUpdateOption = (index: number, label: string) => {
    const newOptions = [...options];
    // Generate value in snake_case from the label, ensure it's never empty
    const value = toSnakeCase(label) || `option_${index + 1}`;
    newOptions[index] = { label, value };
    onChange(newOptions);
  };

  return (
    <div className="space-y-2">
      {options.map((option, index) => (
        <div key={index} className="flex items-center space-x-2">
          <Input
            value={option.label}
            onChange={(e) => handleUpdateOption(index, e.target.value)}
            placeholder={`Option ${index + 1}`}
            disabled={disabled}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => handleRemoveOption(index)}
            disabled={disabled || options.length <= 1}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleAddOption}
        disabled={disabled}
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Option
      </Button>
    </div>
  );
};

export default SelectOptionsEditor;
